import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Logo } from '../components/Logo';

const privacy = {
  title: 'Privacy Policy',
  updated: 'Last updated: 29 August 2026',
  sections: [
    {
      h: 'Who we are',
      p: 'AVENIR Relay ("AVENIR", "we", "us") is the trading name of a sole trader business based at 42A Penrith Road, New Malden, KT3 3QS, United Kingdom. AVENIR operates relaybyavenir.com and provides operational software for connected fleet service. For the purposes of UK data protection law, AVENIR is the controller of the personal data described in this policy. You can contact us at any time at hello@relaybyavenir.com.',
    },
    {
      h: 'What we collect',
      p: 'When you complete our enquiry form we collect the details you provide: your name, work email address, company, job title or role, fleet size, region, organisation type, areas of interest, current systems, your message, your preferred contact method and, where you choose telephone contact, your phone number. Like most websites, our hosting infrastructure also records standard technical logs (such as IP address, browser type and pages visited) for security and performance purposes.',
    },
    {
      h: 'How we use your information',
      p: 'We use the details you submit only to respond to your enquiry, arrange and prepare demonstrations or conversations you have requested, and keep an appropriate record of our correspondence. Our lawful basis is our legitimate interest in responding to business enquiries and, where relevant, taking steps at your request before entering into a contract. We do not use your details for unrelated marketing, and we do not sell personal data.',
    },
    {
      h: 'Who we share it with',
      p: 'Your enquiry is processed by our email delivery provider solely so it can be delivered to our team, and is stored in our hosted systems. We do not share your details with third parties for their own purposes. We may disclose information where required by law.',
    },
    {
      h: 'How long we keep it',
      p: 'We keep enquiry records for up to 24 months so we can manage our conversation with you, unless an ongoing business relationship makes a longer period appropriate, after which they are securely deleted.',
    },
    {
      h: 'Your rights',
      p: 'Under UK GDPR you have the right to access, correct, delete, restrict or object to our use of your personal data, and the right to data portability. To exercise any of these rights, email hello@relaybyavenir.com. You also have the right to complain to the Information Commissioner\u2019s Office (ico.org.uk), though we would welcome the chance to resolve any concern directly first.',
    },
    {
      h: 'Cookies and analytics',
      p: 'This website uses essential storage that is technically required to operate the site. We have also prepared an optional analytics layer: when enabled, privacy-respecting analytics cookies would be used to understand how the site is used, and would only ever load after you give consent through the cookie banner. Analytics are not currently active on this site. Your cookie choice is stored for 12 months, and you can change it at any time using the "Cookie preferences" link in the footer.',
    },
    {
      h: 'Security and international transfers',
      p: 'We apply appropriate technical and organisational measures to protect your information. Where our service providers process data outside the UK, this is done under appropriate safeguards such as standard contractual clauses.',
    },
    {
      h: 'Changes to this policy',
      p: 'We may update this policy from time to time. The current version will always be published on this page.',
    },
  ],
};

const terms = {
  title: 'Terms & Conditions',
  updated: 'Last updated: 29 August 2026',
  sections: [
    {
      h: 'About this website',
      p: 'This website is operated by AVENIR Relay ("AVENIR", "we", "us"), the trading name of a sole trader business based at 42A Penrith Road, New Malden, KT3 3QS, United Kingdom. We can be contacted at hello@relaybyavenir.com. By using relaybyavenir.com you accept these terms.',
    },
    {
      h: 'Information only',
      p: 'The content of this website is provided for general information about AVENIR Relay and its software and services. It does not constitute an offer, advice, or a contractual commitment. Any supply of software or services is subject to a separate written agreement.',
    },
    {
      h: 'Intellectual property',
      p: 'All content on this website — including the AVENIR relay. brand, text, design, imagery and product screenshots — belongs to or is licensed to AVENIR. You may view and share links to this site, but you may not reproduce, reuse or republish its content without our written permission.',
    },
    {
      h: 'Accuracy and availability',
      p: 'We take care to keep the information on this site accurate and up to date, but we provide it "as is" and make no warranties that it is complete, error-free or continuously available. Product capabilities and integration statuses shown are indicative and may change.',
    },
    {
      h: 'Liability',
      p: 'Nothing in these terms excludes or limits our liability for death or personal injury caused by negligence, for fraud, or for anything else that cannot be excluded by law. Otherwise, to the extent permitted by law, we exclude liability for any indirect or consequential loss arising from use of this website.',
    },
    {
      h: 'External links',
      p: 'Where this site links to third-party websites, we are not responsible for their content or their privacy practices.',
    },
    {
      h: 'Governing law',
      p: 'These terms are governed by the laws of England and Wales, and the courts of England and Wales have exclusive jurisdiction over any dispute relating to them.',
    },
  ],
};

export default function Legal({ page }) {
  const doc = page === 'terms' ? terms : privacy;

  useEffect(() => {
    document.title = `${doc.title} | AVENIR Relay`;
    window.scrollTo(0, 0);
    return () => {
      document.title = 'AVENIR Relay | Fleet Operations Software Built Around the Operation';
    };
  }, [doc.title]);

  return (
    <main className="min-h-screen bg-[#FAFAF7]" data-testid="legal-page">
      <div className="container-relay max-w-[760px] py-16 md:py-24">
        <a href="/" data-testid="legal-logo" aria-label="AVENIR Relay — home">
          <Logo />
        </a>
        <h1 className="mt-12 text-3xl font-bold tracking-tight text-[#111111] sm:text-4xl" data-testid="legal-title">
          {doc.title}
        </h1>
        <p className="font-op mt-3 text-[11px] uppercase tracking-[0.22em] text-[#6B7280]">{doc.updated}</p>
        <div className="mt-10 space-y-9">
          {doc.sections.map((s, i) => (
            <section key={s.h} data-testid={`legal-section-${i}`}>
              <h2 className="flex items-baseline gap-3 text-base font-bold tracking-tight text-[#111111]">
                <span className="font-op text-xs font-semibold text-[#0F5C4A]">{String(i + 1).padStart(2, '0')}</span>
                {s.h}
              </h2>
              <p className="mt-2.5 text-sm leading-relaxed text-[#4B5563]">{s.p}</p>
            </section>
          ))}
        </div>
        <a href="/" data-testid="legal-back" className="btn-secondary mt-14">
          <ArrowLeft size={15} /> Back to relaybyavenir.com
        </a>
      </div>
    </main>
  );
}
